import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Factory, AlertTriangle, CalendarDays, ShoppingCart, ClipboardList, Boxes, BookOpen, Wheat, BarChart3, Loader2, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ProdHeader, FabricaGate, StatusBadge, useFabrica, fmt, today, addDays, weekStart, fmtData } from "./shared";

const ATALHOS = [
  { to: "/producao/planejamento", l: "Planejamento", d: "Calendário da semana e ordens", i: CalendarDays },
  { to: "/producao/compras", l: "Compras da semana", d: "Lista consolidada e aprovação", i: ShoppingCart },
  { to: "/producao/requisicoes", l: "Requisições", d: "Reservar e entregar materiais", i: ClipboardList },
  { to: "/producao/estoque", l: "Estoque", d: "Locais, lotes e rastreio", i: Boxes },
  { to: "/producao/fichas", l: "Fichas técnicas", d: "Receitas, versões e custo", i: BookOpen },
  { to: "/produtos?filtro=producao", l: "Materiais", d: "Cadastro único de produtos", i: Wheat },
  { to: "/producao/relatorios", l: "Relatórios", d: "Previsto × real", i: BarChart3 },
];
const NIVEL = { alto: "border-red-500/50 bg-red-500/10 text-red-200", medio: "border-amber-500/40 bg-amber-500/10 text-amber-200" };

const NOMES = { homologacao: "Guia_Homologacao_Producao.md", evidencias: "Evidencias_Homologacao_Producao.md", roteiro: "Roteiro_Homologacao_Nayana.html" };
const baixar = async (nome) => {
  const r = await api.get(`/producao/documentos/${nome}`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url; a.download = NOMES[nome]; a.click();
  URL.revokeObjectURL(url);
};
const imprimir = async () => {
  const w = window.open("", "_blank");
  try {
    const r = await api.get("/producao/documentos/roteiro", { responseType: "text" });
    if (!w) return baixar("roteiro");
    w.document.write(r.data); w.document.close(); w.focus(); setTimeout(() => w.print(), 400);
  } catch (e) { if (w) w.close(); toast.error("Não foi possível abrir o roteiro"); }
};

function Documentos() {
  return (
    <Card className="bg-card/70 p-4 flex flex-wrap items-center gap-3" data-testid="painel-documentos">
      <div className="text-xs font-bold uppercase tracking-wide text-primary mr-auto">Documentos de homologação</div>
      <Button size="sm" variant="outline" onClick={() => baixar("homologacao")} data-testid="doc-homologacao"><FileDown className="w-4 h-4" /> Guia de homologação</Button>
      <Button size="sm" variant="outline" onClick={() => baixar("evidencias")} data-testid="doc-evidencias"><FileDown className="w-4 h-4" /> Evidências dos testes</Button>
      <Button size="sm" onClick={imprimir} data-testid="doc-roteiro-imprimir"><FileDown className="w-4 h-4" /> Imprimir roteiro de teste</Button>
      <Button size="sm" variant="outline" onClick={() => baixar("roteiro")} data-testid="doc-roteiro"><FileDown className="w-4 h-4" /> Baixar roteiro</Button>
    </Card>
  );
}

export default function ProducaoPainel() {
  const fab = useFabrica();
  const [alertas, setAlertas] = useState(null);
  const [ops, setOps] = useState([]);
  useEffect(() => {
    if (!fab.unitId) return;
    const ini = weekStart(today());
    api.get("/producao/alertas", { params: { unit_id: fab.unitId } }).then((r) => setAlertas(r.data)).catch(() => setAlertas([]));
    api.get("/producao/ordens", { params: { unit_id: fab.unitId, ini, fim: addDays(ini, 6) } }).then((r) => setOps(r.data));
  }, [fab.unitId]);
  const cont = (s) => ops.filter((o) => o.status === s).length;
  return (
    <div className="space-y-6" data-testid="prod-painel-page">
      <ProdHeader title="Painel" icon={Factory} fab={fab} />
      <Documentos />
      <FabricaGate fab={fab}>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {["PLANEJADA", "LIBERADA", "EM_PRODUCAO", "CONCLUIDA", "CANCELADA"].map((s, i) => (
            <Card key={s} className="bg-card/70 p-4 animate-in fade-in slide-in-from-bottom-2" style={{ animationDelay: `${i * 60}ms` }} data-testid={`kpi-${s}`}>
              <StatusBadge s={s} /><div className="font-display text-3xl font-extrabold mt-2">{cont(s)}</div><div className="text-[10px] uppercase text-muted-foreground">ordens nesta semana</div>
            </Card>
          ))}
        </div>
        <div className="grid lg:grid-cols-3 gap-4">
          <Card className="bg-card/70 p-4 space-y-2 lg:col-span-2" data-testid="painel-alertas">
            <div className="text-xs font-bold uppercase tracking-wide text-primary flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Alertas</div>
            {!alertas ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : alertas.length ? alertas.slice(0, 30).map((a, i) => (
              <div key={i} className={`text-sm rounded-md border px-3 py-2 ${NIVEL[a.nivel]}`} data-testid={`alerta-${a.tipo}-${i}`}>{a.op_id ? <Link to={`/producao/ordens/${a.op_id}`} className="underline">{a.msg}</Link> : a.msg}</div>
            )) : <div className="text-sm text-muted-foreground">Sem alertas: materiais, validades e fechamentos em dia.</div>}
          </Card>
          <Card className="bg-card/70 p-4 space-y-2" data-testid="painel-hoje">
            <div className="text-xs font-bold uppercase tracking-wide text-primary">Hoje · {fmtData(today())}</div>
            {ops.filter((o) => o.data === today()).map((o) => <Link key={o.id} to={`/producao/ordens/${o.id}`} className="flex justify-between text-sm hover:text-primary"><span>{o.numero} · {o.produto_nome}</span><span className="font-mono">{fmt(o.quantidade)}</span></Link>)}
            {!ops.some((o) => o.data === today()) && <div className="text-sm text-muted-foreground">Nenhuma ordem para hoje.</div>}
          </Card>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {ATALHOS.map((a) => (
            <Link key={a.to} to={a.to} data-testid={`atalho-${a.to.split("/").pop()}`}>
              <Card className="bg-card/70 p-4 h-full hover:border-primary/60 hover:-translate-y-0.5 transition-[transform,border-color] duration-200"><a.i className="w-6 h-6 text-primary mb-2" /><div className="font-semibold">{a.l}</div><div className="text-xs text-muted-foreground">{a.d}</div></Card>
            </Link>
          ))}
        </div>
      </FabricaGate>
    </div>
  );
}
